// The palettes a member can pick between.
//
// This list is the display half of the built-in themes. It is derived from
// lib/builtinPalettes.ts rather than written out again, because that file also
// generates styles/themes.css — so a theme cannot appear in the picker without
// having colours, or have colours without appearing in the picker. The same IDs
// are validated server-side in server/internal/model/theme.go, which is the one
// remaining copy and the one the theme test pins.
//
// A member may also build their own — see lib/palette.ts. Both kinds are named
// by the same `theme` string on the user, so choosing between them is one
// choice, not two.

import type { CustomTheme } from "../api/types";
import { BUILTIN_PALETTES } from "./builtinPalettes";
import { customThemeRef } from "./palette";

export interface ThemeOption {
  id: string;
  name: string;
  /** One line of kid-readable flavour, shown under the swatch. */
  blurb: string;
}

export const THEMES: ThemeOption[] = BUILTIN_PALETTES.map(
  ({ id, name, blurb }) => ({ id, name, blurb }),
);

export const DEFAULT_THEME = "default";

/**
 * Resolves which palette to paint with.
 *
 * A name is only honoured if it still names something: a built-in we ship, or
 * one of this member's own palettes. Anything else — a theme they deleted in
 * another tab, a built-in removed after they chose it — falls back to the
 * default rather than leaving the document unthemed.
 */
export function resolveTheme(
  userTheme: string | undefined,
  customThemes: CustomTheme[],
): string {
  if (!userTheme) return DEFAULT_THEME;

  if (THEMES.some((t) => t.id === userTheme)) return userTheme;
  if (customThemes.some((t) => customThemeRef(t.id) === userTheme)) {
    return userTheme;
  }
  return DEFAULT_THEME;
}
